'use client' // Enables client-side rendering for this component

// Importing necessary types and modules
import { JSXElementConstructor, ReactElement } from 'react'
import { plusJarkartaSans } from './fonts' // Importing custom font configuration
import { ThemeProvider } from 'next-themes' // For managing dark/light themes
import { Provider } from 'react-redux' // Redux provider to give access to global store
import { ToastProviders } from '@school/common/common-utils/providers/toast' // Custom toast notifications
import '@school/styles/globals.css' // Global CSS
import '@school/styles/custom.css' // Project-specific custom styles
import classNames from 'classnames' // Utility for conditionally joining classNames
import { usePathname } from 'next/navigation' // Next.js hook to get current pathname
import { store } from '@school/store/store' // Redux store
import '@school/console-utils/common-utils/assets/css/ag-grid.css' // AG Grid base CSS
import '@school/console-utils/common-utils/assets/css/ag-theme-alpine.css' // AG Grid Alpine theme CSS
import { MetaTitle, defaultMeta } from '@school/common/common-utils/constants/meta-constants' // Metadata constants

export default function RootLayout({
  children,
}: {
  children: ReactElement<unknown, string | JSXElementConstructor<unknown>>
}) {
  // Get the current pathname (e.g., "/dashboard/home")
  const pathname = usePathname()

  // Split the pathname and get the first route segment (e.g., "dashboard")
  const segments = pathname.split('/').filter(Boolean)
  const firstSegment = segments?.[0]

  // Retrieve metadata based on the first segment, or fallback to default
  const metaData = MetaTitle?.[firstSegment] || defaultMeta

  return (
    <html
      lang='en'
      id='student-portal'
      // Apply font and light theme as default
      className={classNames([plusJarkartaSans.variable, 'light'])}
      style={{ colorScheme: 'light' }}
    >
      <head>
        {/* Basic meta tags */}
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
        <title>{metaData?.title + ' - ' + metaData?.subTitle}</title>
        <meta name='description' content={metaData?.subTitle} />
        <link rel='icon' href='/AllenLogoShort.svg' />
        <meta charSet='UTF-8' />
        <meta name='robots' content='index, follow' />
      </head>
      <body>
        {/* Redux provider wraps the entire app to share global state */}
        <Provider store={store}>
          {/* ToastProvider enables toast notifications globally */}
          <ToastProviders>
            {/* ThemeProvider enables theme switching via class attribute */}
            <ThemeProvider attribute='class' defaultTheme='light'>
              {children} {/* Render the application content */}
            </ThemeProvider>
          </ToastProviders>
        </Provider>
      </body>
    </html>
  )
}
